import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { NavComponent } from '../../components/nav.component/nav.component';
import { FooterComponent } from '../../components/footer.component/footer.component';

interface Feature {
  icon: string;
  title: string;
  description: string;
  color: string;
}

interface LaunchPromise {
  icon: string;
  title: string;
  subtitle: string;
}

interface GameTile {
  icon: string;
  title: string;
}

@Component({
  selector: 'app-home.component',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    NavComponent,
    FooterComponent
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent {
  // Whether the "Peek at the lineup" panel is expanded.
  gamesOpen = false;

  // Pre-launch trust strip. Honest at any user count — every line is
  // something we can keep promising forever, and nothing here is invented.
  promises: LaunchPromise[] = [
    {
      icon: 'verified',
      title: 'Every place gets verified',
      subtitle: 'New submissions are matched against real-world places and screened automatically before they land on the map. Edge cases get a second look from a moderator.',
    },
    {
      icon: 'volunteer_activism',
      title: 'Free to find a bathroom',
      subtitle: 'Searching, filtering, and getting directions are free. Optional Premium covers things like ad-free games and extra perks.',
    },
    {
      icon: 'lock',
      title: 'Private by default',
      subtitle: 'Your location is only used while you are searching, and we never sell your data.',
    },
    {
      icon: 'rocket_launch',
      title: 'No noise, just bathrooms',
      subtitle: 'No ads chasing you, no fake reviews, no SEO clutter. Just where to go.',
    },
  ];

  features: Feature[] = [
    {
      icon: 'location_on',
      title: 'Find Bathrooms Fast',
      description: 'Locate the nearest clean bathrooms with friendly summaries and accessibility info.',
      color: 'primary'
    },
    {
      icon: 'search',
      title: 'Smart Search',
      description: 'Filter by 24-hour spots, accessibility, cleanliness, and more.',
      color: 'secondary'
    },
    {
      icon: 'favorite',
      title: 'Save Favorites',
      description: 'Bookmark your go-to spots and get quick access when nature calls.',
      color: 'error'
    },
    {
      icon: 'videogame_asset',
      title: 'Retro Games',
      description: 'Play classic arcade games while you wait. Earn xp and climb leaderboards.',
      color: 'tertiary'
    },
    {
      icon: 'account_circle',
      title: 'Earn Rewards',
      description: 'Write reviews, unlock badges, and win tournaments to get premium features.',
      color: 'primary'
    },
    {
      icon: 'emoji_events',
      title: 'Track High Scores',
      description: 'Compete with friends and the community. Every game saves your personal best.',
      color: 'tertiary'
    }
  ];

  // Curated subset of the in-app lineup, drawn from the real registry.ts
  // names so the marketing copy doesn't drift from the product. Showing 12
  // of the 27 — the bigger names and category leads.
  gamesLineup: GameTile[] = [
    { icon: 'sports_baseball',   title: 'Pong' },
    { icon: 'gesture',           title: 'Snake' },
    { icon: 'memory',            title: 'Memory' },
    { icon: 'flutter_dash',      title: 'Flappy Loo' },
    { icon: 'view_module',       title: '2048' },
    { icon: 'token',             title: 'Whack-a-Mole' },
    { icon: 'quiz',              title: 'Trivia Flush' },
    { icon: 'extension',         title: 'Crossword Throne' },
    { icon: 'tag',               title: 'Tic-Tac-Toe Blitz' },
    { icon: 'palette',           title: 'Color Clash' },
    { icon: 'graphic_eq',        title: 'Flush Beat' },
    { icon: 'rocket',            title: 'Loo Invaders' },
  ];

  scrollToFeatures(): void {
    const element = document.getElementById('features');
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  scrollToGames(): void {
    const element = document.getElementById('games');
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  toggleGamesLineup(): void {
    this.gamesOpen = !this.gamesOpen;
  }

  downloadApp(): void {
    // TODO: Add app store links when available
    console.log('Download app clicked');
  }
}
